import {
    createContext,
    FC,
    PropsWithChildren,
    ReactElement,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { type ActivityResponse, type Feed, FeedsClient, type FeedsClientState } from '@stream-io/feeds-client'
import { useFeedActivities } from '@stream-io/feeds-client/react-bindings'

import { NotificationItem } from '@/entities/Notification/Notification'
import { synchronizeFeedPreservingPagination } from '@/helpers/synchronizeFeedPreservingPagination'
import { useFeedNotifications } from '@/hooks/useFeedNotifications'
import { useSyncOnWindowFocus } from '@/hooks/useSyncOnWindowFocus'
import { setFeedUnreadCount } from '@/redux/notifications/notificationsReducer'
import { useTypedDispatch } from '@/redux/store'

import { useStreamCredentials } from './StreamCredentialsContext'

const apiKey = process.env.REACT_APP_STREAM_API_KEY ?? ''

interface FeedConnectionContextValue {
    client?: FeedsClient
    feed?: Feed
    activities: ActivityResponse[]
    loading: boolean
    hasNextPage: boolean
    isLoadingNextPage: boolean
    loadNextPage: () => void
    notifications: NotificationItem[]
    unreadCount: number
    markAllRead: () => void
    userId: string
    // The credentials, the connection or loading the timeline failed, so the feed won't become available
    hasConnectionError: boolean
}

const FeedConnectionContext = createContext<FeedConnectionContextValue | null>(null)

const noop = (): void => {
}

const createDisconnectedValue = (loading: boolean, hasConnectionError: boolean): FeedConnectionContextValue => ({
    activities: [],
    loading,
    hasConnectionError,
    hasNextPage: false,
    isLoadingNextPage: false,
    loadNextPage: noop,
    notifications: [],
    unreadCount: 0,
    markAllRead: noop,
    userId: '',
})

interface FeedsClientConnection {
    client: FeedsClient | null
    hasConnectionError: boolean
}

/**
 * Like the feeds-client's `useCreateFeedsClient`, but exposes a failed `connectUser` instead of
 * throwing it during render (which, without an error boundary, unmounts the whole app). Only
 * reconnects when the user id or token provider changes.
 */
const useFeedsClient = (userId?: string, tokenProvider?: () => Promise<string>): FeedsClientConnection => {
    const [client, setClient] = useState<FeedsClient | null>(null)
    const [hasConnectionError, setHasConnectionError] = useState(false)

    useEffect(() => {
        if (! userId || ! tokenProvider) {
            return undefined
        }

        const newClient = new FeedsClient(apiKey)
        let interrupted = false

        const connectionPromise = newClient.connectUser({ id: userId }, tokenProvider)
            .then(() => {
                if (! interrupted) {
                    setHasConnectionError(false)
                }
            })
            .catch(error => {
                console.error('[useFeedsClient]', error)

                if (! interrupted) {
                    setHasConnectionError(true)
                }
            })

        setClient(newClient)

        return () => {
            interrupted = true
            setClient(null)
            setHasConnectionError(false)
            connectionPromise.then(() => newClient.disconnectUser()).catch(error => console.error(error))
        }
    }, [userId, tokenProvider])

    return { client, hasConnectionError }
}

/**
 * Owns the single Stream Feeds connection for the whole authenticated session (mounted once in
 * PrivateRouteGroup, not per-page), so unread state is known app-wide - not only while the feed
 * page happens to be open - and the feed page itself doesn't have to open a second connection.
 * Only connects once the current user's Stream credentials are known, but always renders the same
 * tree: swapping it at that moment would remount (and reset) the current page.
 */
export const FeedConnectionProvider: FC<PropsWithChildren> = ({ children }): ReactElement => {
    const dispatch = useTypedDispatch()
    const credentials = useStreamCredentials()
    const userId = credentials.status === 'ready' ? credentials.userId : undefined
    const tokenProvider = credentials.status === 'ready' ? credentials.tokenProvider : undefined
    const { client, hasConnectionError } = useFeedsClient(userId, tokenProvider)

    const [timelineFeed, setTimelineFeed] = useState<Feed>()
    const [hasTimelineError, setHasTimelineError] = useState(false)

    useEffect(() => {
        if (! client || ! userId) {
            return undefined
        }

        let mounted = true
        let started = false

        const unsubscribe = client.state.subscribe(async ({ is_ws_connection_healthy: isHealthy }: FeedsClientState) => {
            if (! isHealthy || started) {
                return
            }

            started = true

            try {
                const timeline = client.feed('timeline', userId)

                // Only applies when the feed gets created; keeps other users from reading this timeline
                await timeline.getOrCreate({ watch: true, data: { visibility: 'private' } })

                if (mounted) {
                    setTimelineFeed(timeline)
                    setHasTimelineError(false)
                }
            } catch (error) {
                // Retried once the connection becomes healthy again
                started = false
                console.error(error)

                if (mounted) {
                    setHasTimelineError(true)
                }
            }
        })

        return () => {
            mounted = false
            unsubscribe()
        }
    }, [client, userId])

    const {
        activities,
        is_loading: isLoading,
        has_next_page: hasNextPage,
        loadNextPage,
    } = useFeedActivities(timelineFeed)

    const { notifications, unreadCount, markAllRead } = useFeedNotifications(activities ?? [], userId ?? '')

    useEffect(() => {
        dispatch(setFeedUnreadCount(unreadCount))
    }, [dispatch, unreadCount])

    useSyncOnWindowFocus(() => {
        if (! client || ! timelineFeed) {
            return
        }

        synchronizeFeedPreservingPagination(client, timelineFeed).catch(error => console.error(error))
    })

    const value = useMemo((): FeedConnectionContextValue => (! userId ? (
        createDisconnectedValue(credentials.status === 'loading', credentials.status === 'unavailable')
    ) : {
        client: client ?? undefined,
        feed: timelineFeed,
        activities: activities ?? [],
        loading: ! client || ! timelineFeed || (!! isLoading && ! activities?.length),
        hasNextPage: !! hasNextPage,
        isLoadingNextPage: !! isLoading && !! activities?.length,
        loadNextPage,
        notifications,
        unreadCount,
        markAllRead,
        userId,
        hasConnectionError: hasConnectionError || (hasTimelineError && ! timelineFeed),
    }), [credentials.status, client, timelineFeed, activities, isLoading, hasNextPage, loadNextPage, notifications, unreadCount, markAllRead, userId, hasConnectionError, hasTimelineError])

    return (
        <FeedConnectionContext.Provider value={value}>
            {children}
        </FeedConnectionContext.Provider>
    )
}

export const useFeedConnection = (): FeedConnectionContextValue => {
    const context = useContext(FeedConnectionContext)

    if (! context) {
        throw new Error('useFeedConnection must be used within a FeedConnectionProvider')
    }

    return context
}
